/**
 * A frame's menu groups (`frame-menu-model.ts`) from what the stores and the
 * frame say, wherever the menu opens: the wall card and Pinned tile, the
 * opened item's ⋯ and right-click, the wall's multi-selection and a desktop
 * widget's ⋯.
 */
import { frameHasCopyText } from "@/components/shapes/shared/frame-copy";
import {
  frameDownloadOptions,
  frameDownloadVaries,
  frameMayDownload,
  type FrameDownloadOption,
} from "@/components/shapes/shared/frame-download";
import {
  widgetSizesToPick,
  widgetSizingFor,
} from "@/components/shapes/shared/widget-sizing";
import { desktopPinFor } from "@/desktop/pin/desktop-pins-store";
import type { FrameData } from "@/lib/frame-data";
import { canPinFrame } from "@/lib/frame-pinning";
import {
  frameMenuGroups,
  frameMenuItems,
  type FrameMenuGroups,
  type FrameMenuSurface,
} from "@/lib/frame-menu-model";
import { isTauri } from "@/lib/is-tauri";
import { linkHere } from "@/lib/local-file-link";
import { previewSource } from "@/lib/frame-preview-source";
import { isFramePinned } from "@/lib/wall-membership";
import { useFramesStore } from "@/stores/frames-store";
import { useSelectionStore } from "@/stores/selection-store";
import {
  selectIsProjectTool,
  selectWallContext,
} from "@/stores/wall-feed-store";
import { foreignWallFrame } from "@/stores/wall-scope-store";
import { wallFrame } from "./wall-frame-source";

/** What the stores say about a frame, read as its menu opens. */
export interface FrameMenuFacts {
  tool: boolean;
  foreign: boolean;
  /** Whether it is pinned where Pin applies; `null` where it does not. */
  pinned: boolean | null;
  /** Whether it is pinned to the desktop; `null` on the web. */
  desktopPinned: boolean | null;
}

export function frameMenuFacts(frameId: string): FrameMenuFacts {
  const state = useFramesStore.getState();
  const own = state.framesById[frameId];
  return {
    tool: selectIsProjectTool(state, frameId),
    foreign: foreignWallFrame(frameId) !== undefined,
    // Another project's card opens in its project, never here.
    pinned:
      own && canPinFrame(own, selectWallContext(state))
        ? isFramePinned(own)
        : null,
    desktopPinned: isTauri() ? desktopPinFor(frameId) !== undefined : null,
  };
}

/** The frame's content items: what Copy and Download have for it. */
export interface FrameMenuContent {
  copy: boolean;
  download: boolean;
  /** A file linked on this computer: Show in Finder or Explorer. */
  showFile: boolean;
  /**
   * A full frame's formats (Download is a choice past one); `null` for a
   * card-only frame, whose one format loads on click.
   */
  options: readonly FrameDownloadOption[] | null;
}

/**
 * A card-only frame copies and downloads once its content loads on click;
 * one whose formats depend on its content (`frameDownloadVaries`) offers
 * Download only once its detail has arrived.
 */
export function frameMenuContent(
  frame: FrameData,
  { root, rendered }: { root: HTMLElement | null; rendered: boolean },
): FrameMenuContent {
  const showFile =
    frame.frameType === "file" && linkHere(previewSource(frame)) !== null;
  if (frame.contentState === "card") {
    return {
      copy: true,
      download:
        !frameDownloadVaries(frame, { rendered }) && frameMayDownload(frame),
      showFile,
      options: null,
    };
  }
  const options = frameDownloadOptions(frame, { rendered });
  return {
    copy: frameHasCopyText(frame, root),
    download: options.length > 0,
    showFile,
    options,
  };
}

interface SingleMenuOptions {
  surface: Exclude<FrameMenuSurface, "selection">;
  facts: FrameMenuFacts;
  content: Pick<FrameMenuContent, "copy" | "download" | "showFile">;
}

/** One frame's groups: Focus where it differs from Open (the desktop). */
export function singleFrameMenuGroups(
  frame: FrameData,
  { surface, facts, content }: SingleMenuOptions,
): FrameMenuGroups {
  return frameMenuGroups({
    surface,
    tool: facts.tool,
    foreign: facts.foreign,
    archived: !!frame.archivedAt,
    focus: isTauri(),
    task: frame.taskTicket !== null,
    copy: content.copy,
    download: content.download,
    showFile: content.showFile,
    pinned: facts.pinned,
    desktopPinned: facts.desktopPinned,
    widgetSizes: widgetSizesToPick(widgetSizingFor(frame.frameType)).length > 0,
  });
}

/**
 * Whether a frame's card menu has an item, read at a right-click: a Pinned
 * tile with nothing to offer lets the click through.
 */
export function cardMenuOpens(frame: FrameData): boolean {
  const { multiSelectedFrameIds } = useSelectionStore.getState();
  if (
    multiSelectedFrameIds.length > 1 &&
    multiSelectedFrameIds.includes(frame.frameId)
  ) {
    return true;
  }
  const groups = singleFrameMenuGroups(frame, {
    surface: "card",
    facts: frameMenuFacts(frame.frameId),
    content: frameMenuContent(frame, { root: null, rendered: false }),
  });
  return frameMenuItems(groups).length > 0;
}

/**
 * Whether an opened item's ⋯ or right-click has an item: Copy selection,
 * its type's extras or the model's. An empty menu lets the right-click
 * through.
 */
export function openedMenuOpens(
  frame: FrameData,
  facts: FrameMenuFacts,
  { extras, selection }: { extras: readonly unknown[]; selection: string },
): boolean {
  if (extras.length > 0 || selection !== "") return true;
  const offers = (
    content: Pick<FrameMenuContent, "copy" | "download" | "showFile">,
  ) =>
    frameMenuItems(
      singleFrameMenuGroups(frame, { surface: "opened", facts, content }),
    ).length > 0;
  // Most items offer Move, Archive or Delete: the content is read only for
  // those that do not (the Project's tools).
  return (
    offers({ copy: false, download: false, showFile: false }) ||
    offers(frameMenuContent(frame, { root: null, rendered: true }))
  );
}

/** Several selected cards: Unarchive only when every one is archived. */
export function selectionMenuGroups(
  frameIds: readonly string[],
): FrameMenuGroups {
  const frames = frameIds.flatMap((id) => wallFrame(id) ?? []);
  const state = useFramesStore.getState();
  return frameMenuGroups({
    surface: "selection",
    tool: frameIds.some((id) => selectIsProjectTool(state, id)),
    foreign: frameIds.some((id) => foreignWallFrame(id) !== undefined),
    archived: frames.length > 0 && frames.every((frame) => !!frame.archivedAt),
    focus: true,
    task: false,
    copy: true,
    download: false,
    showFile: false,
    pinned: null,
    desktopPinned: null,
    widgetSizes: false,
  });
}
